import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Modal } from '@/components/dialogs/Modal';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { formatNumber } from '@/utils/format';
import { filterDataApi } from '@/api/services/filterData';
import { suspiciousTradesApi } from '@/api/services/suspiciousTrades';
import {
  type TraderSurveillanceAlertDto,
  type TsAlertFilterDto,
  type TsAlertTradeRowDto,
  type TsAuditTrailRowDto,
} from '@/api/types/traderSurveillance';
import { AlertInfoPanel } from './AlertInfoPanel';
import { useSuspiciousTradesHub } from './useSuspiciousTradesHub';
import { renderDateTime, renderTsPriority, renderTsStatus } from './renderers';

const PRIORITY_OPTS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];
const STATUS_OPTS = [
  { value: '', label: 'All' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Resolved' },
];

const DEFAULT_TITLE = 'Alerts Arisen';

interface DraftFilters {
  startDate: string;
  endDate: string;
  priority: string[];
  connector: string[];
  country: string[];
  profileTagId: string[];
  status: string;
  uniqueId: string;
  login: string;
}

const emptyDraft = (): DraftFilters => ({
  startDate: '',
  endDate: '',
  priority: [],
  connector: [],
  country: [],
  profileTagId: [],
  status: '',
  uniqueId: '',
  login: '',
});

// Build the base TsAlertFilterDto from the draft (counts use this; the grid adds a
// status override). Only non-empty fields are sent (legacy readFilters).
function readFilters(d: DraftFilters): TsAlertFilterDto {
  const f: TsAlertFilterDto = {};
  if (d.startDate) f.startDate = d.startDate;
  if (d.endDate) f.endDate = d.endDate;
  if (d.uniqueId) f.uniqueId = d.uniqueId;
  if (d.login) f.login = parseInt(d.login, 10);
  if (d.priority.length) f.priority = d.priority;
  if (d.connector.length) f.connector = d.connector.map(Number);
  if (d.country.length) f.country = d.country;
  if (d.profileTagId.length) f.profileTagId = d.profileTagId;
  if (d.status !== '') f.status = parseInt(d.status, 10);
  return f;
}

export function SuspiciousTradesPage() {
  const toast = useToast();
  const can = usePermission();
  const canOperate = can('User.TraderSurveillance.AlertsProfileOperations');

  const [draft, setDraft] = useState<DraftFilters>(emptyDraft);
  const [applied, setApplied] = useState<DraftFilters>(emptyDraft);
  // KPI status override: undefined = none; null/0/1 overrides the grid status.
  const [statusOverride, setStatusOverride] = useState<number | null | undefined>(undefined);
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [infoId, setInfoId] = useState<number | null>(null);
  const [tradeOpen, setTradeOpen] = useState<number | null>(null);
  const [auditOpen, setAuditOpen] = useState<number | null>(null);
  const [resolveAllOpen, setResolveAllOpen] = useState(false);
  const [resolveAllComment, setResolveAllComment] = useState('');

  const alertsColRef = useRef<HTMLDivElement>(null);
  const tradeColRef = useRef<HTMLDivElement>(null);
  const auditColRef = useRef<HTMLDivElement>(null);

  const profileTags = useQuery({ queryKey: ['ts-profile-tags'], queryFn: suspiciousTradesApi.getProfileTagOptions, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getConnectors, staleTime: 5 * 60_000 });
  // ST country filter uses the country NAME as the value (List<string>), not the id.
  const countries = useQuery({ queryKey: ['ts-countries'], queryFn: filterDataApi.getCountries, staleTime: 5 * 60_000 });

  const countryOpts = useMemo(
    () => (countries.data ?? []).map((c) => ({ value: c.label, label: c.label })),
    [countries.data],
  );
  const profileTagOpts = useMemo(
    () => (profileTags.data ?? []).map((p) => ({ value: p.value, label: p.text })),
    [profileTags.data],
  );

  // Grid filter = applied base filters + the KPI status override.
  const gridFilter = useMemo<TsAlertFilterDto>(() => {
    const base = readFilters(applied);
    if (statusOverride !== undefined) {
      if (statusOverride === null) delete base.status;
      else base.status = statusOverride;
    }
    return base;
  }, [applied, statusOverride]);

  const table = useServerDataTable<TraderSurveillanceAlertDto, TsAlertFilterDto>({
    queryKey: ['ts-alerts'],
    fetcher: suspiciousTradesApi.getAlerts,
    pageSize: 5,
    extraParams: gridFilter,
  });

  // Counts always use the base filters (ignore the status override).
  const counts = useQuery({
    queryKey: ['ts-alert-counts', applied],
    queryFn: () => suspiciousTradesApi.getAlertCounts(readFilters(applied)),
  });

  useSuspiciousTradesHub({
    enabled: true,
    onPendingCountChanged: () => {
      table.refetch();
      counts.refetch();
    },
  });

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    window.setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  function fetch() {
    setApplied(draft);
    setStatusOverride(undefined);
    setTitle(DEFAULT_TITLE);
  }
  function clear() {
    setDraft(emptyDraft());
    setApplied(emptyDraft());
    setStatusOverride(undefined);
    setTitle(DEFAULT_TITLE);
  }
  function kpiClick(override: number | null, newTitle: string) {
    setInfoId(null);
    setStatusOverride(override);
    setTitle(newTitle);
    scrollTo(alertsColRef);
  }

  function reloadAll() {
    table.refetch();
    counts.refetch();
  }

  async function submitResolveAll() {
    if (!resolveAllComment.trim()) {
      toast.warning('Please add a comment.');
      return;
    }
    try {
      await suspiciousTradesApi.resolveAll({ ...readFilters(applied), status: 0, comment: resolveAllComment.trim() });
      toast.success('Your request has been received. All matching alerts will be resolved shortly.');
      setResolveAllOpen(false);
      setResolveAllComment('');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'The request failed.');
    }
  }

  const str = (v: string) => v;
  const fields: (ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker mode="range" value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="priority">
      <label className="form-label">Priority</label>
      <Select2Multi placeholder="All" value={draft.priority} options={PRIORITY_OPTS} onChange={(v) => setDraft((f) => ({ ...f, priority: v }))} />
    </div>,
    <div key="connector">
      <label className="form-label">Connector</label>
      <Select2Multi placeholder="All" value={draft.connector} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connector: v }))} />
    </div>,
    <div key="country">
      <label className="form-label">Country</label>
      <Select2Multi placeholder="All" value={draft.country} options={countryOpts} onChange={(v) => setDraft((f) => ({ ...f, country: v }))} />
    </div>,
    <div key="ptag">
      <label className="form-label">Profile Tag</label>
      <Select2Multi placeholder="All" value={draft.profileTagId} options={profileTagOpts} onChange={(v) => setDraft((f) => ({ ...f, profileTagId: v }))} />
    </div>,
    <Select2 key="status" label="Status" allowClear={false} searchable={false} value={str(draft.status)} options={STATUS_OPTS} onChange={(v) => setDraft((f) => ({ ...f, status: v ?? '' }))} />,
    <div key="uniqueId">
      <label className="form-label">Unique Id</label>
      <input className="form-control" value={draft.uniqueId} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value }))} />
    </div>,
    <div key="login">
      <label className="form-label">Login</label>
      <input className="form-control" inputMode="numeric" value={draft.login} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value }))} />
    </div>,
  ];

  // Column order matches the legacy Suspicious Trades alerts grid exactly.
  const alertsCols: DataGridColumn<TraderSurveillanceAlertDto>[] = [
    { key: 'id', title: 'Alert ID', sortable: false, value: (r) => r.id },
    { key: 'profileTag', title: 'Profile Tag', value: (r) => r.profileTag },
    { key: 'priority', title: 'Priority', value: (r) => r.priority, cell: (r) => renderTsPriority(r.priority) },
    { key: 'uniqueId', title: 'Unique ID', value: (r) => r.uniqueId },
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'susDate', title: 'Alert Date', value: (r) => r.susDate, cell: (r) => renderDateTime(r.susDate) },
    { key: 'statusName', title: 'Status', value: (r) => r.statusName, cell: (r) => renderTsStatus(r.statusName) },
    { key: 'resolveDate', title: 'Resolve Date', value: (r) => r.resolveDate, cell: (r) => renderDateTime(r.resolveDate) },
    { key: 'commentDate', title: 'Comment Date', value: (r) => r.commentDate, cell: (r) => renderDateTime(r.commentDate) },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];

  const infoOpen = infoId !== null;

  return (
    <div className="suspicious-trades-page">
      <PageHeader title="Suspicious Trades" breadcrumb={['Home', 'Trader Surveillance', 'Suspicious Trades']} />

      <FilterBar
        fields={fields}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={fetch}>
              Fetch
            </AsyncButton>
            <button type="button" className="btn btn-link btn-sm" onClick={clear}>
              Clear All Filters
            </button>
          </>
        }
      />

      <div className="row mb-2">
        <KpiCard label="Related Unique Ids" value={counts.data?.relatedUniqueIdsCount} icon="fa fa-fingerprint" variant="primary" onClick={() => kpiClick(null, DEFAULT_TITLE)} />
        <KpiCard label="Alerts Pending" value={counts.data?.alertsPendingCount} icon="fa fa-clock" variant="warning" onClick={() => kpiClick(0, 'Alerts Pending')} />
        <KpiCard label="Alerts Arisen" value={counts.data?.alertsArisenCount} icon="fa fa-bell" variant="info" onClick={() => kpiClick(null, DEFAULT_TITLE)} />
        <KpiCard label="Alerts Resolved" value={counts.data?.alertsResolvedCount} icon="fa fa-check" variant="success" onClick={() => kpiClick(1, 'Alerts Resolved')} />
      </div>

      <div className="row mb-4 g-2">
        <div ref={alertsColRef} className={infoOpen ? 'col-lg-8' : 'col-lg-12'}>
          <div className="custom-card card h-100">
            <div className="card-header d-flex justify-content-between align-items-center pb-3 pt-2 border-0">
              <h6 className="mb-0">{title}</h6>
              {canOperate && (
                <button type="button" className="btn btn-outline-success btn-sm" onClick={() => setResolveAllOpen(true)}>
                  Resolve All
                </button>
              )}
            </div>
            <div className="card-body">
              <DataGrid
                columns={alertsCols}
                data={table.rows}
                rowKey={(r) => String(r.id)}
                columnSearch
                columnSearchValues={table.columnFilters}
                onColumnSearchChange={(key, value) => table.setColumnFilter(key, value)}
                includeExcel
                excelFileName="Suspicious Trades"
                emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
                rowActions={(r) => (
                  <button type="button" className="btn btn-sm btn-outline-info" title="Info" onClick={() => setInfoId(r.id)}>
                    <i className="fa fa-info-circle" />
                  </button>
                )}
                server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
              />
            </div>
          </div>
        </div>

        {infoOpen && infoId !== null && (
          <div className="col-lg-4">
            <AlertInfoPanel
              alertId={infoId}
              canOperate={canOperate}
              onClose={() => setInfoId(null)}
              onChanged={reloadAll}
              onShowTrade={() => {
                setTradeOpen(infoId);
                scrollTo(tradeColRef);
              }}
              onShowAudit={() => {
                setAuditOpen(infoId);
                scrollTo(auditColRef);
              }}
            />
          </div>
        )}
      </div>

      {tradeOpen !== null && (
        <div ref={tradeColRef}>
          <TradeGrid alertId={tradeOpen} onClose={() => setTradeOpen(null)} />
        </div>
      )}

      {auditOpen !== null && (
        <div ref={auditColRef}>
          <AuditGrid alertId={auditOpen} onClose={() => setAuditOpen(null)} />
        </div>
      )}

      <Modal
        show={resolveAllOpen}
        title="Resolve All Alerts"
        onClose={() => setResolveAllOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-outline-dark" onClick={() => setResolveAllOpen(false)}>
              Cancel
            </button>
            <AsyncButton className="btn btn-primary" onClick={submitResolveAll}>
              Resolve All
            </AsyncButton>
          </>
        }
      >
        <p>This action will resolve all alerts arisen. Are you sure you want to resolve them? If so, kindly add below the comment.</p>
        <textarea className="form-control" rows={4} value={resolveAllComment} onChange={(e) => setResolveAllComment(e.target.value)} />
      </Modal>
    </div>
  );
}

function KpiCard({ label, value, icon, variant, onClick }: { label: string; value?: number; icon: string; variant: string; onClick: () => void }) {
  return (
    <div className="col-xl-3 col-md-6">
      <div className="custom-card card cursor-pointer" role="button" onClick={onClick}>
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <p className="mb-1 text-muted fs-13">{label}</p>
            <h4 className="mb-0 fw-semibold">{value ?? '—'}</h4>
          </div>
          <span className={`avatar avatar-md rounded bg-${variant} text-fixed-white`}>
            <i className={icon} />
          </span>
        </div>
      </div>
    </div>
  );
}

function TradeGrid({ alertId, onClose }: { alertId: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ['ts-alert-trade', alertId], queryFn: () => suspiciousTradesApi.getAlertTradeRows(alertId) });
  const rows = q.data ?? [];
  const title = rows.length && rows[0].isDeal ? 'Deal' : 'Trade';
  const num = (v?: number | null) => formatNumber(v ?? 0, 2);

  const cols: DataGridColumn<TsAlertTradeRowDto>[] = [
    { key: 'ticket', title: 'Ticket', value: (r) => r.ticket },
    { key: 'name', title: 'Name', value: (r) => r.name },
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'currency', title: 'Currency', value: (r) => r.currency },
    { key: 'type', title: 'Type', value: (r) => r.type },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => num(r.volume) },
    { key: 'instrument', title: 'Instrument', value: (r) => r.instrument },
    { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => num(r.openPrice) },
    { key: 'openTime', title: 'Open Time', value: (r) => r.openTime, cell: (r) => renderDateTime(r.openTime) },
    { key: 'closePrice', title: 'Close Price', className: 'text-end', value: (r) => r.closePrice, cell: (r) => num(r.closePrice) },
    { key: 'closeTime', title: 'Close Time', value: (r) => r.closeTime, cell: (r) => renderDateTime(r.closeTime) },
    { key: 'profit', title: 'Profit', className: 'text-end', value: (r) => r.profit, cell: (r) => num(r.profit) },
    { key: 'profitRc', title: 'Profit (RC)', className: 'text-end', value: (r) => r.profitRc, cell: (r) => num(r.profitRc) },
    { key: 'notional', title: 'Notional', className: 'text-end', value: (r) => r.notional, cell: (r) => num(r.notional) },
    { key: 'notionalRc', title: 'Notional (RC)', className: 'text-end', value: (r) => r.notionalRc, cell: (r) => num(r.notionalRc) },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];

  return (
    <div className="custom-card card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center border-0">
        <h6 className="mb-0">{title}</h6>
        <button type="button" className="btn btn-sm" title="Close" onClick={onClose}>
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="card-body p-0">
        <DataGrid
          columns={cols}
          data={rows}
          rowKey={(r) => `${r.ticket}-${r.connectorId}`}
          pageSize={5}
          columnVisibilityToggle={false}
          includeExcel
          excelFileName="Alert Trade"
          emptyMessage={q.isLoading ? 'Loading…' : 'No data available'}
        />
      </div>
    </div>
  );
}

function AuditGrid({ alertId, onClose }: { alertId: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ['ts-alert-audit', alertId], queryFn: () => suspiciousTradesApi.getAuditTrail(alertId) });
  const rows = q.data ?? [];

  const cols: DataGridColumn<TsAuditTrailRowDto>[] = [
    { key: 'date', title: 'Date', value: (r) => r.date, cell: (r) => renderDateTime(r.date) },
    { key: 'user', title: 'User', value: (r) => r.user },
    { key: 'action', title: 'Action', value: (r) => r.action },
    { key: 'newComment', title: 'New Comment', value: (r) => r.newComment },
    { key: 'oldComment', title: 'Previous Comment', value: (r) => r.oldComment },
  ];

  return (
    <div className="custom-card card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center border-0">
        <h6 className="mb-0">Audit Trail</h6>
        <button type="button" className="btn btn-sm" title="Close" onClick={onClose}>
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="card-body p-0">
        <DataGrid
          columns={cols}
          data={rows}
          rowKey={(r) => `${r.date}-${r.action}`}
          pageSize={5}
          columnVisibilityToggle={false}
          includeExcel
          excelFileName="Alert Audit Trail"
          emptyMessage={q.isLoading ? 'Loading…' : 'No data available'}
        />
      </div>
    </div>
  );
}
